import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRightIcon,
  CpuChipIcon,
  DocumentTextIcon,
  FolderIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { getTeamUuid, getUserUuid } from "../../utils/cookies";
import GuideLinks from "../../Components/GeneralComponents/GuideLinks";
import {
  getTeamLatestFiles,
  getTeamMembers,
  getTeamStatistics,
  updateCredits,
} from "../../Requests/TeamRequests";
import { getProjects } from "../../Requests/ProjectRequests";
import { useMyContext } from "../../Components/ContextProviders/CreditsContextProvider";
import { formatDateTime } from "../../utils/calcs";
import { getNextStep } from "../../utils/nextStep";

// The project a user last opened, written by DetailProject.
function getLastProject() {
  try {
    return JSON.parse(localStorage.getItem("last_project"));
  } catch (e) {
    return null;
  }
}

function Tile({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-4 rounded-xl bg-gray-50 px-4 py-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-black text-white">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-semibold leading-tight text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function Panel({ title, subtitle, action, children }) {
  return (
    <div className="flex h-full flex-col justify-between gap-6 rounded-2xl bg-white p-5 shadow-xl">
      <div className="flex flex-col gap-3">{children}</div>
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        {action && <div className="mt-3 flex justify-end">{action}</div>}
      </div>
    </div>
  );
}

function Button({ onClick, children, variant = "solid" }) {
  const styles =
    variant === "solid"
      ? "bg-black text-white hover:bg-gray-800"
      : "border border-gray-300 text-gray-900 hover:bg-gray-50";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${styles}`}
    >
      {children}
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { credits, setCredits } = useMyContext();

  const [statistics, setStatistics] = useState(null);
  const [members, setMembers] = useState(null);
  const [latestFiles, setLatestFiles] = useState(null);
  const [projects, setProjects] = useState(null);
  const [lastProject, setLastProject] = useState(null);
  const [nextStep, setNextStep] = useState(null);

  useEffect(() => {
    const team_uuid = getTeamUuid();

    getTeamStatistics(team_uuid).then((res) => setStatistics(res.data)).catch(() => { });
    updateCredits(team_uuid).then((res) => setCredits(res.data)).catch(() => { });
    getTeamMembers(team_uuid).then((res) => setMembers(res.data["members"])).catch(() => { });
    getTeamLatestFiles(team_uuid).then((res) => setLatestFiles(res.data)).catch(() => { });

    getProjects(team_uuid).then((res) => {
      setProjects(res.data);
      // The last project opened on this device, falling back to the newest one.
      const stored = getLastProject();
      const target = res.data.find((p) => p.uuid === stored?.uuid) || res.data[0];
      if (!target) return;
      setLastProject(target);
      setNextStep(getNextStep(target));
    }).catch(() => { });
  }, []);

  const me = members?.find((member) => member.uuid === getUserUuid());
  const firstName = me?.name ? me.name.split(" ")[0] : t("Dashboard.GreetingFallbackName");
  const NextStepIcon = nextStep?.icon || FolderIcon;

  return (
    <div className="md:pl-64 pt-4 md:pt-12 w-full flex justify-center px-4 pb-12">
      <div className="flex w-full max-w-7xl flex-col gap-4 md:px-4">

        <div className="rounded-2xl bg-white px-6 py-6 shadow-xl">
          <h1 className="font-display text-2xl text-gray-900 sm:text-3xl">
            {t("Dashboard.Greeting", { name: firstName })}
          </h1>
          <p className="mt-1 text-sm text-gray-500">{t("Dashboard.GreetingSubtitle")}</p>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

          <div className="lg:col-span-2">
            <Panel
              title={t("Dashboard.ContinueTitle")}
              subtitle={t("Dashboard.ContinueSubtitle")}
              action={
                lastProject && (
                  <Button
                    onClick={() =>
                      navigate(`/project/${lastProject.uuid}${nextStep ? `?tab=${nextStep.tab}` : ""}`)
                    }
                  >
                    {t("Dashboard.ContinueAction")} <ArrowRightIcon className="h-4 w-4" />
                  </Button>
                )
              }
            >
              {lastProject ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-4 rounded-xl bg-gray-50 px-4 py-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-black text-white">
                      <FolderIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-lg font-semibold text-gray-900">{lastProject.name}</div>
                      <div className="truncate text-sm text-gray-500">{lastProject.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 rounded-xl border border-dashed border-gray-300 px-4 py-3">
                    <NextStepIcon className="h-6 w-6 shrink-0 text-gray-900" />
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">
                        {t("Dashboard.NextStepLabel")}
                      </div>
                      <div className="text-sm font-semibold text-gray-900">
                        {nextStep ? t(`Dashboard.NextStep${nextStep.key}`) : t("Dashboard.NextStepLoading")}
                      </div>
                      {nextStep && (
                        <div className="text-sm text-gray-500">
                          {t(`Dashboard.NextStep${nextStep.key}Description`, { files: nextStep.files })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-start gap-3 rounded-xl bg-gray-50 px-4 py-6">
                  <p className="text-sm text-gray-500">{t("Dashboard.ContinueEmpty")}</p>
                  <Button onClick={() => navigate("/projects")}>{t("Dashboard.CreateProject")}</Button>
                </div>
              )}
            </Panel>
          </div>

          <Panel
            title={t("Dashboard.LatestFilesTitle")}
            subtitle={t("Dashboard.LatestFilesSubtitle")}
            action={<Button onClick={() => navigate("/files")}>{t("Dashboard.AllFiles")}</Button>}
          >
            {latestFiles && latestFiles.length === 0 && (
              <p className="text-sm text-gray-500">{t("Dashboard.NoFiles")}</p>
            )}
            {(latestFiles || []).map((file) => (
              <button
                key={file.uuid}
                type="button"
                onClick={() =>
                  navigate(
                    file.status === "Not processed"
                      ? `/project/${file.project?.uuid}?tab=0`
                      : "/analysis/" + file.uuid
                  )
                }
                className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2 text-left hover:bg-gray-100"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black text-white">
                  <DocumentTextIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-900">{file.name}</div>
                  <div className="truncate text-xs text-gray-500">
                    {file.project?.name} · {formatDateTime(file.created_at)}
                  </div>
                </div>
              </button>
            ))}
          </Panel>

          <div className="lg:col-span-2">
            <Panel
              title={t("Dashboard.ProjectsOverviewTitle")}
              subtitle={t("Dashboard.ProjectsOverviewSubtitle")}
              action={<Button onClick={() => navigate("/projects")}>{t("Dashboard.AllProjects")}</Button>}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Tile
                  icon={FolderIcon}
                  value={statistics ? statistics.projects : "—"}
                  label={t("Dashboard.ActiveProjectsLabel")}
                />
                <Tile
                  icon={DocumentTextIcon}
                  value={statistics ? statistics.files : "—"}
                  label={t("Dashboard.ProcessedFilesLabel")}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(projects || []).slice(0, 6).map((project) => (
                  <button
                    key={project.uuid}
                    type="button"
                    onClick={() => navigate("/project/" + project.uuid)}
                    className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-3 text-left hover:bg-gray-100"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black text-white">
                      <FolderIcon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-900">{project.name}</div>
                      <div className="truncate text-xs text-gray-500">{project.description}</div>
                    </div>
                  </button>
                ))}
                {projects && projects.length === 0 && (
                  <p className="text-sm text-gray-500">{t("Dashboard.NoProjects")}</p>
                )}
              </div>
            </Panel>
          </div>

          <Panel
            title={t("Dashboard.CreditsTitle")}
            subtitle={t("Dashboard.CreditsSubtitle")}
            action={
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={() => navigate("/team")}>
                  {t("Dashboard.ManageTeam")}
                </Button>
                <Button onClick={() => navigate("/pricing/credits")}>{t("Dashboard.GetCredits")}</Button>
              </div>
            }
          >
            <Tile
              icon={CpuChipIcon}
              value={credits !== undefined && credits !== null ? Number(credits).toFixed(2) : "—"}
              label={t("Dashboard.CreditsAvailableLabel")}
            />
            <Tile
              icon={UserGroupIcon}
              value={members ? members.length : "—"}
              label={t("Dashboard.TeamMembersLabel")}
            />
          </Panel>

        </div>
        <GuideLinks page="dashboard" className="mt-4" />
      </div>
    </div>
  );
}
